'use client';

import { useCallback } from 'react';
import type { DomainTree } from '@terra/ui-design-system';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import { terraProxyApiClient } from '../../clients/testerClient';

const parseEndpointNodeSelection = (nodeId: string) => {
  if (!nodeId.startsWith('endpoint:')) {
    return null;
  }

  const parts = nodeId.split(':');
  if (parts.length < 5) {
    return null;
  }

  const assetId = parts[parts.length - 1];
  const methodIndex = parts.length === 6 ? 3 : parts.length - 2;
  const method = parts[methodIndex];
  const path = parts.slice(2, methodIndex).join(':');

  return { assetId, method, path };
};

const parseWebPageNodeSelection = (nodeId: string) => {
  if (!nodeId.startsWith('webpage:')) {
    return null;
  }

  const parts = nodeId.split(':');
  return parts[parts.length - 1] ?? null;
};

type UseDomainTreePanelSelectionParams = {
  domainTree: DomainTree | null;
  selectedNodes: string[];
  setSelectedNodes: (nodes: string[]) => void;
  selectionMode: 'multiple' | 'single';
  onEndpointSelect?: (endpoint: {
    id: string;
    path: string;
    method?: string;
    data?: EndpointReturnDto;
  }) => void;
  onWebPageSelect?: (webpage: {
    id: string;
    url: string;
    data?: WebPageWithSignedScreenshotReturnDto;
  }) => void;
};

export function useDomainTreePanelSelection({
  domainTree,
  selectedNodes,
  setSelectedNodes,
  selectionMode,
  onEndpointSelect,
  onWebPageSelect,
}: UseDomainTreePanelSelectionParams) {
  const openSelectedEndpoint = useCallback(
    async ({ nodeId }: { nodeId: string }) => {
      if (!onEndpointSelect) {
        return;
      }

      const parsedSelection = parseEndpointNodeSelection(nodeId);
      if (!parsedSelection) {
        return;
      }

      try {
        const matchingEndpoint = await terraProxyApiClient.getEndpointById(
          parsedSelection.assetId,
        );

        if (matchingEndpoint?.id) {
          onEndpointSelect({
            id: matchingEndpoint.id,
            path: parsedSelection.path,
            method: parsedSelection.method,
            data: matchingEndpoint,
          });
          return;
        }
      } catch (error) {
        console.warn('DomainTreePanel: Failed to load endpoint for asset ID', {
          error: error instanceof Error ? error : new Error(String(error)),
          props: {
            assetId: parsedSelection.assetId,
            nodeId,
          },
        });
      }

      console.warn('DomainTreePanel: Could not find endpoint for asset ID:', {
        assetId: parsedSelection.assetId,
        path: parsedSelection.path,
        method: parsedSelection.method,
        nodeId,
      });
    },
    [onEndpointSelect],
  );

  const openSelectedWebPage = useCallback(
    async ({ webPageId }: { webPageId: string }) => {
      if (!onWebPageSelect) {
        return;
      }

      try {
        const matchingWebpage =
          await terraProxyApiClient.getWebPageById(webPageId);

        if (matchingWebpage) {
          onWebPageSelect({
            id: matchingWebpage.id,
            url: matchingWebpage.url,
            data: matchingWebpage,
          });
        }
      } catch (error) {
        console.warn('DomainTreePanel: Failed to load webpage for asset ID', {
          error: error instanceof Error ? error : new Error(String(error)),
          props: { webPageId },
        });
      }
    },
    [onWebPageSelect],
  );

  const openSelectionForNode = useCallback(
    (nodeId: string) => {
      if (nodeId.startsWith('endpoint:')) {
        void openSelectedEndpoint({ nodeId });
      } else if (nodeId.startsWith('webpage:')) {
        const webPageId = parseWebPageNodeSelection(nodeId);
        if (webPageId) {
          void openSelectedWebPage({ webPageId });
        }
      } else if (nodeId.startsWith('path:')) {
        const clickedNode = domainTree?.nodeMap.get(nodeId);
        const firstWebPageId = clickedNode?.directWebPageIds[0];
        if (firstWebPageId) {
          void openSelectedWebPage({ webPageId: firstWebPageId });
        }
      }
    },
    [domainTree, openSelectedEndpoint, openSelectedWebPage],
  );

  const handleDomainSelectionChange = useCallback(
    (domains: string[]) => {
      const isLeafNode = (nodeId: string): boolean => {
        const node = domainTree?.nodeMap.get(nodeId);
        return node ? node.children.length === 0 : false;
      };

      if (selectionMode === 'single') {
        if (domains.length === 0) {
          setSelectedNodes([]);
          return;
        }

        const clickedItem = domains[domains.length - 1];

        if (!isLeafNode(clickedItem)) {
          return;
        }

        if (selectedNodes.includes(clickedItem)) {
          setSelectedNodes([]);
          return;
        }

        setSelectedNodes([clickedItem]);
        openSelectionForNode(clickedItem);
        return;
      }

      setSelectedNodes(domains);

      const newlySelected = domains.find(
        (domain) => !selectedNodes.includes(domain),
      );

      if (newlySelected && isLeafNode(newlySelected)) {
        openSelectionForNode(newlySelected);
      }
    },
    [
      domainTree,
      openSelectionForNode,
      selectedNodes,
      selectionMode,
      setSelectedNodes,
    ],
  );

  return { handleDomainSelectionChange };
}
